// src/TVQueue.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Modal, Form, InputNumber, Button, message } from "antd";
import QRCode from "react-qr-code";
import { listOpenOrders } from "../services/pos";
import api from "../services/api";
import { logoutToPin } from "../services/session";
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "../utils/tauri";

/** --------- SETTINGS --------- */
type TVSettings = {
  splitPercentLeft: number; // % lijevog panela (20-80)
  appBg: string;
  leftBg: string;
  rightBg: string;

  // GRID
  prepPerRow: number;   // Brojeva po redu - U PRIPREMI
  readyPerRow: number;  // Brojeva po redu - SPREMNO
  rowHeight: number;    // Visina reda (px)
  gridGap: number;      // Razmak između brojeva (px)

  // Boje brojeva
  prepNumberColor: string;
  readyNumberColor: string;

  // Labele
  labelFontSize: number;
  prepLabelColor: string;
  readyLabelColor: string;
  prepLabelTextColor: string;
  readyLabelTextColor: string;
  labelBgAlpha: number; // 0..1
};

const LS_SETTINGS = "tvq.settings";
const DEFAULTS: TVSettings = {
  splitPercentLeft: 66,
  appBg: "#0b0f1a",
  leftBg: "transparent",
  rightBg: "transparent",

  // GRID
  prepPerRow: 4,
  readyPerRow: 5,
  rowHeight: 140,
  gridGap: 12,

  // BOJE BROJEVA
  prepNumberColor: "#f59e0b", // amber-500
  readyNumberColor: "#10b981", // emerald-500

  // LABELE
  labelFontSize: 24,
  prepLabelColor: "#dbb418",
  readyLabelColor: "#22c55e",
  prepLabelTextColor: "#ffffff",
  readyLabelTextColor: "#ffffff",
  labelBgAlpha: 0.18,
};

function hexToRgba(hex: string, alpha = 1) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function useTVSettings() {
  const [s, setS] = useState<TVSettings>(() => {
    try {
      const raw = localStorage.getItem(LS_SETTINGS);
      return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
    } catch {
      return DEFAULTS;
    }
  });
  const save = (next: TVSettings) => {
    setS(next);
    localStorage.setItem(LS_SETTINGS, JSON.stringify(next));
  };
  const reset = () => save({ ...DEFAULTS });
  return { s, save, reset };
}

/** --------- PAIRING (QR) --------- */
const ADMIN_URL =
  (window as any).__ADMIN_URL__ ??
  localStorage.getItem("admin_panel_url") ??
  "https://portal.itable.app";

// Keys to check/set in localStorage for device UUID
const LS_DEVICE_UUID_KEYS = ["device_uuid"];

/** ---- ANIM/ARRIVAL konstante ---- */
type Status = "prep" | "ready";
type AnimKind = "fly-right" | "fly-left" | "pop-prep" | "pop-ready";
const ANIM_MS = 800;     // trajanje animacije prelaza/prilaska
const FRESH_MS = 5000;   // koliko dugo je “druga boja” nakon dolaska

// “druga boja” (samo 5s)
const FRESH_PREP_COLOR = "#961c01ff";  // amber-300 (svjetlije od #f59e0b)
const FRESH_READY_COLOR = "#ffffffff"; // emerald-400 (svjetlije od #10b981)

/** --------- KOMPONENTA --------- */
export default function TVQueue() {
  const [orders, setOrders] = useState<any[]>([]);
  const { s, save, reset } = useTVSettings();
  const [openSettings, setOpenSettings] = useState(false);
  const [formSettings] = Form.useForm<TVSettings>();

  // Pairing modal
  const alreadyPaired = LS_DEVICE_UUID_KEYS.some((k) => !!localStorage.getItem(k));
  const [pairOpen, setPairOpen] = useState(!alreadyPaired);
  const [pairCode, setPairCode] = useState<string>("");
  const [checking, setChecking] = useState(false);

  // Animacije samo za elemente koji su se pomjerili ili došli
  const prevStatusRef = useRef<Map<number | string, Status>>(new Map());
  const [animMap, setAnimMap] = useState<Record<string, AnimKind>>({});
  const [arrivedAt, setArrivedAt] = useState<Record<string, number>>({});
  const [nowTick, setNowTick] = useState<number>(Date.now()); // da “otkuca” fresh boju

  // lagani timer da istekne FRESH_MS i osvježi UI boju
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  const getDeviceCode = () => {
    if (isTauri()) {
      try {
        const code = invoke<string>("get_machine_code");
        return code;
      } catch {
        return "TAURI_ERR";
      }
    } else {
      // fallback za web/React dev
      let code = localStorage.getItem("tv.uuid_device_code");
      if (!code) {
        code = "WEB" + Math.random().toString(36).substring(2, 8).toUpperCase();
        localStorage.setItem("tv.uuid_device_code", code);
      }
      return code;
    }
  };

  // Učitaj stabilni 10-znakovni kod sa hardvera (Tauri cmd get_machine_code)
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const code = await getDeviceCode();
        if (!stop && code) {
          setPairCode(code as any);
          localStorage.setItem("kiosk.device_uuid_paring", code as any);
        }
      } catch (e) {
        setPairCode("");
      }
    })();
    return () => { stop = true; };
  }, []);

  const pairLink = pairCode
    ? `${ADMIN_URL}/pair-device?code=${encodeURIComponent(
        pairCode
      )}&kind=tv&server=${encodeURIComponent(window.location.origin)}`
    : "";

  // --- ORDERS REFRESH ---
  async function refresh() {
    if (pairOpen) return; // ne diraj dok se ne upari
    try {
      const data = await listOpenOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      // @ts-ignore
      console.error("TVQueue refresh error:", e?.message || e);
    }
  }

  useEffect(() => {
    refresh(); // odmah
    const t = setInterval(refresh, 5_000); // svake 5s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling za pairing status (ne dira refresh narudžbi)
  useEffect(() => {
    if (!pairOpen || !pairCode) return; // čekaj da imamo kod
    let stop = false;

    async function tick() {
      try {
        setChecking(true);
        const resp = await api.get(`/pos/device/pairing/status?code=${encodeURIComponent(pairCode)}`);
        const json = resp?.data || {};
        if (json?.status === "completed" && json?.uuid) {
          LS_DEVICE_UUID_KEYS.forEach((k) => localStorage.setItem(k, json.uuid));
          message.success("Uređaj uspješno uparen.");
          setPairOpen(false);
          refresh();
        }
      } catch {
        // tih
      } finally {
        setChecking(false);
      }
      if (!stop) setTimeout(tick, 2000);
    }

    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairOpen, pairCode]);

  useEffect(() => {
    if (openSettings) formSettings.setFieldsValue(s);
  }, [openSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const prep = useMemo(() => orders.filter((o) => o.status === "in_preparation"), [orders]);
  const ready = useMemo(() => orders.filter((o) => o.status === "ready").slice(-30), [orders]);

  /** --------- DETEKCIJA PRELAZA + REGISTRACIJA ARRIVAL --------- */
  useEffect(() => {
    const nextStatus = new Map<number | string, Status>();
    const nextAnim: Record<string, AnimKind> = {};
    const now = Date.now();

    // skup svih trenutnih ID-eva (radi čišćenja)
    const currentIds = new Set<string>();

    for (const o of prep) {
      const key: number | string = o.id ?? o.number;
      const id = (key ?? "").toString();
      currentIds.add(id);
      const prev = prevStatusRef.current.get(key);
      nextStatus.set(key, "prep");

      if (!prev) {
        // novi u sistemu => pop u lijevoj + zabilježi dolazak
        nextAnim[id] = "pop-prep";
      } else if (prev === "ready") {
        // prešao iz desne u lijevu
        nextAnim[id] = "fly-left";
      }
    }

    for (const o of ready) {
      const key: number | string = o.id ?? o.number;
      const id = (key ?? "").toString();
      currentIds.add(id);
      const prev = prevStatusRef.current.get(key);
      nextStatus.set(key, "ready");

      if (!prev) {
        // novi u sistemu => pop u desnoj + zabilježi dolazak
        nextAnim[id] = "pop-ready";
      } else if (prev === "prep") {
        // prešao iz lijeve u desnu
        nextAnim[id] = "fly-right";
      }
    }

    // Postavi animacije samo za pogođene elemente; upiši njihov arrival timestamp
    if (Object.keys(nextAnim).length) {
      setAnimMap((old) => ({ ...old, ...nextAnim }));
      setArrivedAt((old) => {
        const copy = { ...old };
        for (const id of Object.keys(nextAnim)) copy[id] = now; // start 5s “fresh” boje
        return copy;
      });

      // makni anim-klasu nakon završetka animacije
      const t = setTimeout(() => {
        setAnimMap((old) => {
          const copy = { ...old };
          for (const id of Object.keys(nextAnim)) delete copy[id];
          return copy;
        });
      }, ANIM_MS + 50);
      const clean = () => clearTimeout(t);
      // ažuriraj prev statuse tek nakon što smo postavili animacije
      prevStatusRef.current = nextStatus;
      // očisti stare arrival zapise koji više nisu na ekranu
      setArrivedAt((old) => {
        const copy = { ...old };
        for (const id of Object.keys(copy)) if (!currentIds.has(id)) delete copy[id];
        return copy;
      });
      return clean;
    }

    // nema novih animacija, ali svejedno ažuriraj prev mape i očisti arrival za nestale
    prevStatusRef.current = nextStatus;
    setArrivedAt((old) => {
      const copy = { ...old };
      for (const id of Object.keys(copy)) if (!currentIds.has(id)) delete copy[id];
      return copy;
    });
  }, [prep, ready]);

  // Veličina fonta izvedena iz visine reda (sigurnosni min)
  const prepFontPx = Math.max(18, Math.floor((s.rowHeight || 120) * 0.64));
  const readyFontPx = Math.max(18, Math.floor((s.rowHeight || 120) * 0.60));

  const labelBox = (bgHex: string, textColor: string, clickable = false) => ({
    fontSize: s.labelFontSize,
    fontWeight: 900,
    textAlign: "center" as const,
    marginBottom: 12,
    padding: "6px 10px",
    borderRadius: 12,
    background: hexToRgba(bgHex, s.labelBgAlpha),
    border: `1px solid ${hexToRgba(bgHex, Math.min(1, s.labelBgAlpha * 2))}`,
    color: textColor,
    userSelect: "none" as const,
    cursor: clickable ? ("pointer" as const) : ("default" as const),
  });

  function confirmLogout() {
    Modal.confirm({
      title: "Odjava uređaja",
      content: "Da li želiš da se odjaviš i vratiš na PIN ekran?",
      okText: "Odjavi",
      cancelText: "Otkaži",
      onOk: async () => {
        try {
          await logoutToPin();
        } catch {}
      },
    });
  }

  // pomoćna da li je broj “svježe došao”
  const isFresh = (id: string) => {
    const ts = arrivedAt[id];
    return !!ts && nowTick - ts < FRESH_MS;
  };

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        background: s.appBg,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* CSS animacije samo za targetirane brojeve */}
      <style>{`
        @keyframes flyRight {
          0% { transform: translateX(-40px) scale(0.85); opacity: 0; }
          60% { transform: translateX(6px) scale(1.12); opacity: 1; }
          100% { transform: translateX(0) scale(1); opacity: 1; }
        }
        @keyframes flyLeft {
          0% { transform: translateX(40px) scale(0.85); opacity: 0; }
          60% { transform: translateX(-6px) scale(1.12); opacity: 1; }
          100% { transform: translateX(0) scale(1); opacity: 1; }
        }
        @keyframes popIn {
          0% { transform: scale(0.6); opacity: 0; }
          60% { transform: scale(1.18); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .anim-fly-right { animation: flyRight ${ANIM_MS}ms cubic-bezier(.2,.8,.2,1) both; }
        .anim-fly-left  { animation: flyLeft  ${ANIM_MS}ms cubic-bezier(.2,.8,.2,1) both; }
        .anim-pop       { animation: popIn   ${ANIM_MS}ms cubic-bezier(.2,.8,.2,1) both; }
      `}</style>

      {/* GLAVNI EKRAN */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${s.splitPercentLeft}% ${100 - s.splitPercentLeft}%`,
          gap: 16,
          padding: 16,
          height: "100%",
        }}
      >
        {/* U PRIPREMI (lijevo) — klik = LOGOUT */}
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", background: s.leftBg, borderRadius: 12 }}>
          <div
            style={labelBox(s.prepLabelColor, s.prepLabelTextColor, true)}
            onClick={confirmLogout}
            title="Klik za odjavu (PIN)"
          >
            U PRIPREMI
          </div>
          <div style={{ flex: 1, overflow: "auto", paddingRight: 6 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${s.prepPerRow}, 1fr)`,
                gridAutoRows: `${s.rowHeight}px`,
                gap: s.gridGap,
              }}
            >
              {prep.map((o) => {
                const id = (o.id ?? o.number ?? "").toString();
                const anim =
                  animMap[id] === "fly-left" ? "anim-fly-left"
                  : animMap[id] === "pop-prep" ? "anim-pop"
                  : "";

                const fresh = isFresh(id);
                const color = fresh ? FRESH_PREP_COLOR : s.prepNumberColor;

                return (
                  <Card
                    key={o.id}
                    className={anim || undefined}
                    style={{
                      background: s.leftBg,
                      border: "0px solid rgba(255,255,255,0.12)",
                      borderRadius: 18,
                      height: "100%",
                      padding: 0,
                      margin:4,
                      display: "grid",
                      placeItems: "center",
                      boxShadow: "none",
                      transition: "box-shadow 200ms ease",
                    }}
                  >
                    <div
                      style={{
                        color,
                        fontSize: Math.round(prepFontPx-2),
                        fontWeight: 900,
                        lineHeight: 1,
                        textAlign: "center",
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {o.number || String(o.id).padStart(4, "0")}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        {/* SPREMNO (desno) — klik = SETTINGS */}
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", background: s.rightBg, borderRadius: 12 }}>
          <div
            onClick={() => setOpenSettings(true)}
            title="Klik za Settings"
            style={labelBox(s.readyLabelColor, s.readyLabelTextColor, true)}
          >
            SPREMNO
          </div>
          <div style={{ flex: 1, overflow: "auto", paddingLeft: 6 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${s.readyPerRow}, 1fr)`,
                gridAutoRows: `${s.rowHeight}px`,
                gap: s.gridGap,
              }}
            >
              {ready.map((o) => {
                const id = (o.id ?? o.number ?? "").toString();
                const anim =
                  animMap[id] === "fly-right" ? "anim-fly-right"
                  : animMap[id] === "pop-ready" ? "anim-pop"
                  : "";

                const fresh = isFresh(id);
                const color = fresh ? FRESH_READY_COLOR : s.readyNumberColor;

                return (
                  <Card
                    key={o.id}
                    className={anim || undefined}
                    style={{
                      background: s.rightBg,
                      marginTop: 3,
                      border: "0px",
                      borderRadius: 18,
                      alignItems: "center",
                      height: "100%",
                      padding: 0,
                      display: "grid",
                      placeItems: "center",
                      boxShadow:  "none",
                      transition: "box-shadow 200ms ease",
                    }}
                  >
                    <div
                      style={{
                        fontSize: Math.round(readyFontPx),
                        fontWeight: 900,
                        verticalAlign: "middle",
                        textAlign: "center",
                        color,
                        lineHeight: 1.05,
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {o.number || String(o.id).padStart(4, "0")}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* SETTINGS MODAL */}
      <Modal
        open={openSettings}
        onCancel={() => setOpenSettings(false)}
        title="TV Queue Settings"
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button danger onClick={reset}>Reset na podrazumijevano</Button>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                type="primary"
                onClick={() => {
                  const values = formSettings.getFieldsValue() as TVSettings;
                  save({ ...s, ...values });
                  setOpenSettings(false);
                }}
              >
                Sačuvaj
              </Button>
              <Button
                danger
                onClick={() => {
                  localStorage.clear();
                  window.location.reload();
                }}
              >
                Očisti podatke i restart
              </Button>
            </div>
          </div>
        }
      >
        <Form form={formSettings} layout="vertical" initialValues={s}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item label="Omjer ekrana – lijevo (%)" name="splitPercentLeft">
              <InputNumber min={20} max={80} step={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Veličina naslova (labela)" name="labelFontSize">
              <InputNumber min={14} max={64} step={1} style={{ width: "100%" }} />
            </Form.Item>

            {/* GRID */}
            <Form.Item label="Brojeva po redu – U PRIPREMI" name="prepPerRow">
              <InputNumber min={1} max={12} step={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Brojeva po redu – SPREMNO" name="readyPerRow">
              <InputNumber min={1} max={12} step={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Visina reda (px)" name="rowHeight">
              <InputNumber min={60} max={320} step={5} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Razmak između brojeva (px)" name="gridGap">
              <InputNumber min={0} max={48} step={1} style={{ width: "100%" }} />
            </Form.Item>

            {/* BOJE I POZADINE */}
            <Form.Item label="Pozadina aplikacije" name="appBg">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>
            <Form.Item label="Pozadina lijevo" name="leftBg">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>
            <Form.Item label="Pozadina desno" name="rightBg">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>

            <Form.Item label="Boja brojeva – U PRIPREMI" name="prepNumberColor">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>
            <Form.Item label="Boja brojeva – SPREMNO" name="readyNumberColor">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>

            <Form.Item label="Boja labele – U PRIPREMI" name="prepLabelColor">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>
            <Form.Item label="Tekst labele – U PRIPREMI" name="prepLabelTextColor">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>
            <Form.Item label="Boja labele – SPREMNO" name="readyLabelColor">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>
            <Form.Item label="Tekst labele – SPREMNO" name="readyLabelTextColor">
              <input type="color" style={{ width: "100%", height: 36, border: "1px solid #333" }} />
            </Form.Item>
            <Form.Item label="Prozirnost pozadine labela" name="labelBgAlpha">
              <InputNumber min={0} max={1} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* FIRST-TIME PAIRING MODAL (QR) */}
      <Modal
        open={pairOpen}
        closable={false}
        maskClosable={false}
        title="Povezivanje uređaja"
        footer={
          <div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Button danger onClick={() => setPairOpen(false)}>Otkaži uparivanje</Button>
              <Button
                onClick={() => {
                  localStorage.clear();
                  window.location.reload();
                }}
              >
                Nazad
              </Button>
              <div style={{ flex: 1 }} >
                <Button onClick={() => pairLink ? window.open(pairLink, "_blank") : null} disabled={!pairLink}>
                  {pairLink ? "Otvori link na ovom uređaju" : "Učitavanje koda..."}
                </Button>
                <Button
                  danger
                  onClick={() => {
                    localStorage.clear();
                    window.location.reload();
                  }}
                >
                  Očisti podatke i restart
                </Button>
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
              {checking ? "Provjera..." : (pairLink ? "Skeniraj QR kod ili otvori link na drugom uređaju." : "Priprema koda uređaja...")}
            </div>
          </div>
        }
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div>📱 Na telefonu (ulogovan u Admin-FE) skeniraj QR i dodijeli ovaj uređaj.</div>
          <div style={{ background: "#fff", padding: 12, borderRadius: 12, display: "grid", placeItems: "center" }}>
            <QRCode value={pairLink || "loading"} size={256} />
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, wordBreak: "break-all" }}>
            {pairLink || "Učitavanje koda..."}
          </div>
        </div>
      </Modal>
    </div>
  );
}
